/**
 * 複習門(🔁)—— 學習端入口整併(spec ★定案、guardrail #3 #7 #8)。
 *
 * 吃掉舊入口:錯題本 + 複習中心(出卷類移到「練」)+ 單字聽寫。
 * **不重寫**既有功能,只重組入口:
 *   - **第一眼 = 「我的錯題」**(琥珀 hero 大卡,點一下 → `renderMistakeBook`,≤2 tap)。
 *   - **今日到期**(Leitner `totalDueCount` 露臉:「今天到期 N 題」)→ 也進錯題本複習。
 *   - 英文聽寫(`runVocabInput`,含聽寫)。
 *   - 批改複習考卷(拍照,`runGradeReview`)、考試規劃(`openExamPlanner`)。
 *   - **空狀態**:沒到期 / 沒錯題 → 「目前沒有要複習的,去『練』一張吧」可一鍵跳「練」。
 *
 * 「複」字三下偏難 → 配 🔁 圖示 + 琥珀色 + 副標「我寫錯的」三重線索(guardrail #3)。
 * 視覺 = 學術風;學習/遊戲分離:純學習側,絕不碰 commanderXp / 戰鬥數值。
 */

import { pageShell, academicCard } from '../ui/components';
import { loadProgress } from '../../shared/progress';
import { totalDueCount } from '../spaced-review';
import { DOORS } from './doors';
import { renderMistakeBook } from '../mistake-book';
import { runVocabInput } from '../review/vocab-input';
import { runGradeReview } from '../review/grade-review';
import { openExamPlanner } from '../exam-plan/exam-review';

/** 複習門依賴的子流程(預設真實 import,測試可注入 stub)。 */
export interface ReviewDoorDeps {
  loadProgress: typeof loadProgress;
  totalDueCount: typeof totalDueCount;
  runMistakeBook: (c: HTMLElement, onBack: () => void) => void;
  runVocab: (c: HTMLElement, onDone: () => void) => void;
  runGradeReview: (c: HTMLElement, onDone: () => void) => void;
  openExamPlanner: (c: HTMLElement, onDone: () => void) => void;
}

/**
 * 複習門入口。
 * @param onBack   離開複習門(回首頁)。
 * @param goPractice 空狀態「去練一張」跳「練」門。
 */
export function runReviewDoor(
  container: HTMLElement,
  onBack: () => void,
  goPractice: () => void,
  deps?: Partial<ReviewDoorDeps>,
): void {
  const d: ReviewDoorDeps = {
    loadProgress,
    totalDueCount,
    runMistakeBook: renderMistakeBook,
    runVocab: runVocabInput,
    runGradeReview,
    openExamPlanner,
    ...deps,
  };
  renderMenu(container, onBack, goPractice, d);
}

function renderMenu(
  container: HTMLElement,
  onBack: () => void,
  goPractice: () => void,
  d: ReviewDoorDeps,
): void {
  container.innerHTML = '';
  const spec = DOORS.review;
  const shell = pageShell(container, {
    title: '🔁 複習',
    subtitle: '我寫錯的、該回頭的,集中在這裡再練一次。',
    onBack,
  });
  shell.root.style.setProperty('--door-accent', spec.accent);

  const open = (run: (c: HTMLElement, done: () => void) => void): void => {
    shell.unmount();
    container.innerHTML = '';
    run(container, () => renderMenu(container, onBack, goPractice, d));
  };

  const root = document.createElement('div');
  root.className = 'review-door';
  root.setAttribute('data-testid', 'review-door');
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  const progress = d.loadProgress();
  const mistakeCount = (progress?.mistakes ?? []).filter((m) => !m.retried).length;
  const dueCount = progress ? d.totalDueCount(progress) : 0;
  const hasReviewWork = mistakeCount > 0 || dueCount > 0;

  // ── 第一眼:我的錯題 hero 大卡(≤2 tap:door → 此卡 → 錯題本)。 ──
  const hero = document.createElement('div');
  hero.className = 'ef-review-hero';
  hero.id = 'review-mistakes';
  hero.setAttribute('role', 'button');
  hero.setAttribute('tabindex', '0');
  hero.setAttribute('aria-label', `我的錯題:${mistakeCount} 題`);
  hero.innerHTML =
    `<div class="ef-review-hero__count" aria-hidden="true">${mistakeCount}</div>` +
    '<div style="display:flex;flex-direction:column;gap:3px;min-width:0;">' +
    '<div style="font-family:var(--ef-serif);font-weight:700;font-size:20px;color:var(--ef-ink);">📌 我的錯題</div>' +
    `<div style="font-size:13px;color:var(--ef-sub);">${mistakeCount > 0 ? '寫錯的題目都在這,點開重練' : '還沒有錯題,寫過卷才會收進來'}</div>` +
    '</div>' +
    '<div style="margin-left:auto;font-size:20px;color:#b07a2e;opacity:0.65;" aria-hidden="true">›</div>';
  const openMistakes = (): void => open((c, done) => d.runMistakeBook(c, done));
  hero.addEventListener('click', openMistakes);
  hero.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openMistakes(); }
  });
  root.appendChild(hero);

  // ── 今日到期(Leitner)——「今天到期 N 題」,點了也進錯題本複習。 ──
  const due = academicCard({
    icon: '📅',
    label: dueCount > 0 ? `今天到期 ${dueCount} 題` : '今天沒有到期的',
    sub: dueCount > 0 ? '間隔到了該回頭複習的題目' : '把間隔複習都跟上了,很棒',
    onClick: () => open((c, done) => d.runMistakeBook(c, done)),
  });
  due.id = 'review-due';
  due.setAttribute('data-due-count', String(dueCount));
  due.classList.add('ef-card--accent');
  due.style.setProperty('--door-accent', spec.accent);
  root.appendChild(due);

  // ── 英文聽寫 ──
  const dictation = academicCard({
    icon: '🎧',
    label: '英文單字 / 聽寫',
    sub: '輸入單字,用聽寫練拼字',
    onClick: () => open((c, done) => d.runVocab(c, done)),
  });
  dictation.id = 'review-dictation';
  dictation.classList.add('ef-card--accent');
  dictation.style.setProperty('--door-accent', spec.accent);
  root.appendChild(dictation);

  // ── 進階複習工具(摺疊):批改複習考卷(拍照)/ 考試規劃。≤5 項。 ──
  const moreWrap = document.createElement('div');
  moreWrap.style.cssText = 'display:flex;flex-direction:column;gap:10px;';
  root.appendChild(moreWrap);

  const moreToggle = document.createElement('button');
  moreToggle.type = 'button';
  moreToggle.id = 'review-more-toggle';
  moreToggle.className = 'ef-btn ef-btn--secondary';
  moreToggle.style.alignSelf = 'flex-start';
  moreToggle.textContent = '更多複習工具 ▼';
  moreWrap.appendChild(moreToggle);

  const moreList = document.createElement('div');
  moreList.id = 'review-more-list';
  moreList.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  moreWrap.appendChild(moreList);

  const grade = academicCard({
    icon: '✅',
    label: '批改複習考卷(拍照)',
    sub: '拍下寫完的複習卷,自動批改、錯題回流',
    onClick: () => open((c, done) => d.runGradeReview(c, done)),
  });
  grade.id = 'review-grade';
  moreList.appendChild(grade);

  const plan = academicCard({
    icon: '🗓️',
    label: '考試規劃',
    sub: '填考試日期,撈範圍內錯題生複習卷',
    onClick: () => open((c, done) => d.openExamPlanner(c, done)),
  });
  plan.id = 'review-plan';
  moreList.appendChild(plan);

  moreToggle.addEventListener('click', () => {
    const isOpen = moreList.style.display !== 'none';
    moreList.style.display = isOpen ? 'none' : 'flex';
    moreToggle.textContent = isOpen ? '更多複習工具 ▼' : '收起 ▲';
  });

  // ── 空狀態引導(guardrail #7):沒到期 / 沒錯題 → 去「練」一張。 ──
  if (!hasReviewWork) {
    const empty = document.createElement('div');
    empty.id = 'review-empty';
    empty.setAttribute('data-testid', 'review-empty');
    empty.style.cssText =
      'margin-top:6px;padding:16px 18px;border:1px dashed var(--ef-line);border-radius:var(--ef-radius);' +
      'background:var(--ef-paper);color:var(--ef-sub);font-size:14px;line-height:1.6;text-align:center;';
    const msg = document.createElement('div');
    msg.textContent = '目前沒有要複習的,去「練」一張吧。';
    empty.appendChild(msg);
    const goBtn = document.createElement('button');
    goBtn.type = 'button';
    goBtn.id = 'review-empty-go-practice';
    goBtn.className = 'ef-btn ef-btn--primary';
    goBtn.style.marginTop = '10px';
    goBtn.textContent = '✏️ 去練一張';
    goBtn.addEventListener('click', () => {
      shell.unmount();
      goPractice();
    });
    empty.appendChild(goBtn);
    root.appendChild(empty);
  }
}
